import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../config";
import { Helmet } from "react-helmet";

export default function FundDetails() {
  const navigate = useNavigate();

  // -------------------------- CURRENT FY FUNCTION --------------------------
  function getCurrentFY() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month < 4 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  }

  const getFYDates = (fy) => {
    const [start, end] = fy.split("-");
    return {
      fy_start: `${start}-04-01`,
      fy_end: `${end}-03-31`,
    };
  };

  const getFYList = () => {
    const [start, end] = getCurrentFY().split("-").map(Number);
    return [
      `${start - 1}-${end - 1}`,
      `${start}-${end}`,
      `${start + 1}-${end + 1}`,
    ];
  };

  const financialYears = getFYList();

  // -------------------------- LOAD INITIAL FILTER VALUES --------------------------
  const saved = JSON.parse(sessionStorage.getItem("fundDetailsFilters") || "{}");

  const selectedDistrictInit = saved.district || "0";
  const selectedFYInit = saved.fy || getCurrentFY();

  // -------------------------- FILTER STATES --------------------------
  const [selectedDistrict, setSelectedDistrict] = useState(selectedDistrictInit);
  const [selectedFY, setSelectedFY] = useState(selectedFYInit);

  const [appliedDistrict, setAppliedDistrict] = useState(selectedDistrictInit);
  const [appliedFY, setAppliedFY] = useState(selectedFYInit);

  const [districts, setDistricts] = useState([]);
  const [fundData, setFundData] = useState([]);

  // -------------------------- LOAD DISTRICT LIST --------------------------
  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((res) => setDistricts(res.data));
  }, []);

  // -------------------------- LOAD FUND DATA FUNCTION --------------------------
  const loadFundData = async (districtValue, fyValue) => {
    const fyObj = getFYDates(fyValue);

    const res = await axios.get(`${API_BASE}/fund-allocation-summary`, {
      params: {
        dist: districtValue,
        fy_start: fyObj.fy_start,
        fy_end: fyObj.fy_end,
      },
    });

    setFundData(res.data);
  };

  // -------------------------- AUTO LOAD ON PAGE OPEN --------------------------
  useEffect(() => {
    loadFundData(selectedDistrictInit, selectedFYInit);
  }, []);

  // -------------------------- SEARCH BUTTON FIXED --------------------------
  const handleSearch = () => {
    setAppliedDistrict(selectedDistrict);
    setAppliedFY(selectedFY);

    loadFundData(selectedDistrict, selectedFY);
  };

  // -------------------------- EXPORT TO EXCEL --------------------------
  const exportToExcel = () => {
    const sheet = fundData.map((row) => ({
      District: row.district_name,
      "Total Allocation (₹)": Number(row.total_amount),
      "Allocated To":
        row.school_status === 1
          ? "All School"
          : row.school_status === 2
          ? "Selected School"
          : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheet);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FundDetails");
    XLSX.writeFile(workbook, "Fund_Details.xlsx");
  };

  // -------------------------- HEADING LABEL --------------------------
  // -------------------------- HEADING LABEL --------------------------
const appliedDistrictName =
appliedDistrict === "0"
  ? "All"
  : districts.find((d) => String(d.dist_id) === String(appliedDistrict))
      ?.district || "All";

const headingLabel = (
<span>
  Fund Allocation Status (
  District:{" "}
  <span style={{ color: "green", fontWeight: 600 }}>
    {appliedDistrictName}
  </span>
  {" | "} FY:{" "}
  <span style={{ color: "green", fontWeight: 600 }}>
    {appliedFY}
  </span>
  )
</span>
);


  // -------------------------- PAGE UI --------------------------
  return (
    <>
        <Helmet>
          <title>Fund Allocation Details</title>
        </Helmet>

    <div style={{ padding: 30, background: "#f7f8fa", minHeight: "100vh" }}>
      {/* -------------------------- TOP HEADER -------------------------- */}
      <div
        style={{
          background: "#ffffff",
          padding: "20px 25px",
          borderRadius: 12,
          marginBottom: 25,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, color: "#283593" }}>Fund Allocation Details</h2>

          <div style={{ display: "flex", gap: 15 }}>
            {/* BACK BUTTON */}
            <button
             onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate("/funds-status");
                }
              }}
              
              style={{
                padding: "10px 18px",
                background: "#9e2c2c",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Back
            </button>

            {/* EXPORT BUTTON */}
            <button
              onClick={exportToExcel}
              style={{
                padding: "10px 18px",
                background: "#2e7d32",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Export to Excel
            </button>
          </div>
        </div>

        {/* -------------------------- FILTERS -------------------------- */}
        <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
          {/* District */}
          <label>
            District:
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              style={{ marginLeft: 10, padding: 6 }}
            >
              <option value="0">All</option>
              {districts.map((d) => (
                <option key={d.dist_id} value={d.dist_id}>
                  {d.district}
                </option>
              ))}
            </select>
          </label>

          {/* FY */}
          <label>
            FY:
            <select
              value={selectedFY}
              onChange={(e) => setSelectedFY(e.target.value)}
              style={{ marginLeft: 10, padding: 6 }}
            >
              {financialYears.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>
          </label>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            style={{
              padding: "10px 25px",
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </div>
      </div>

      {/* -------------------------- HEADING -------------------------- */}
      <h2 style={{ marginBottom: 20, color: "#283593", fontSize: "1.1em" }}>
        {headingLabel}
      </h2>

      {/* -------------------------- TABLE -------------------------- */}
      <div
        style={{
          background: "#fff",
          padding: 20,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
         <style>{`
  .tblview th {
     text-align: left;
    padding: 12px;
    background: #283593;
    color: white;
  }
`}</style>
        {fundData.length === 0 ? (
          <p>No Records Found</p>
        ) : (

          
          <table className="tblview" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#283593", color: "white" }}>
                <th style={{ padding: 12 }}>District</th>
                <th style={{ padding: 12 }}>Total Allocation (₹)</th>
                <th style={{ padding: 12 }}>Allocated To</th>
              </tr>
            </thead>

            <tbody>
              {fundData.map((row) => (
                <tr
                  key={row.dist_id}
                  style={{ borderBottom: "1px solid #ddd" }}
                >
                  <td style={{ padding: 12 }}>{row.district_name}</td>

                  <td style={{ padding: 12 }}>
                    ₹{Number(row.total_amount).toLocaleString()}
                  </td>

                  <td style={{ padding: 12 }}>
                    {row.school_status === 1
                      ? "All School"
                      : row.school_status === 2
                      ? "Selected School"
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </>
  );
}
